// src/pages/reportes/Reportes.js
import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts'

const COLORS = ['#22c55e','#3b82f6','#f59e0b','#ef4444','#8b5cf6','#0891b2','#ec4899','#14b8a6']

export default function Reportes() {
  const [finMeses, setFinMeses] = useState([])
  const [animalesPorEspecie, setAnimalesPorEspecie] = useState([])
  const [cultivosStats, setCultivosStats] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { loadReportes() }, [])

  async function loadReportes() {
    setLoading(true)
    const año = new Date().getFullYear()
    const inicioAño = `${año}-01-01`

    const [finRes, animRes, cultRes] = await Promise.all([
      supabase.from('transacciones').select('tipo, monto, fecha').gte('fecha', inicioAño),
      supabase.from('v_animales_resumen').select('*'),
      supabase.from('cultivos').select('*, tipo:tipos_cultivo(nombre)').eq('estado', 'cosechado'),
    ])

    // Finanzas por mes
    const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']
    const byMonth = Array.from({ length: 12 }, (_, i) => ({ mes: meses[i], ingresos: 0, gastos: 0, neto: 0 }))
    finRes.data?.forEach(t => {
      const m = new Date(t.fecha).getMonth()
      if (t.tipo === 'ingreso') byMonth[m].ingresos += Number(t.monto)
      else byMonth[m].gastos += Number(t.monto)
      byMonth[m].neto = byMonth[m].ingresos - byMonth[m].gastos
    })
    setFinMeses(byMonth.slice(0, new Date().getMonth() + 1))

    // Animales por especie
    const especieMap = {}
    animRes.data?.filter(a => a.estado === 'activo').forEach(a => {
      especieMap[a.especie] = (especieMap[a.especie] ?? 0) + Number(a.cantidad)
    })
    setAnimalesPorEspecie(Object.entries(especieMap).map(([name, value]) => ({ name, value })))

    // Cultivos cosechados
    const cultMap = {}
    cultRes.data?.forEach(c => {
      const nombre = c.tipo?.nombre ?? 'Otro'
      if (!cultMap[nombre]) cultMap[nombre] = { nombre, hectareas: 0, rendimiento: 0, count: 0 }
      cultMap[nombre].hectareas += Number(c.area_ha ?? 0)
      cultMap[nombre].rendimiento += Number(c.rendimiento_kg ?? 0)
      cultMap[nombre].count++
    })
    setCultivosStats(Object.values(cultMap))

    setLoading(false)
  }

  async function exportarExcel() {
    const { data } = await supabase.from('transacciones')
      .select('fecha, tipo, descripcion, monto, referencia')
      .order('fecha', { ascending: false })

    if (!data?.length) return alert('Sin datos para exportar')

    const XLSX = await import('xlsx')
    const ws = XLSX.utils.json_to_sheet(data.map(t => ({
      Fecha: t.fecha,
      Tipo: t.tipo,
      Descripción: t.descripcion,
      Monto: t.monto,
      Referencia: t.referencia ?? '',
    })))
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Transacciones')
    XLSX.writeFile(wb, `summint-finanzas-${new Date().toISOString().slice(0,10)}.xlsx`)
  }

  async function exportarAnimales() {
    const { data } = await supabase.from('animales')
      .select('codigo, nombre, sexo, estado, fecha_nacimiento, peso_kg, color')
      .order('codigo')

    if (!data?.length) return alert('Sin datos para exportar')

    const XLSX = await import('xlsx')
    const ws = XLSX.utils.json_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Animales')
    XLSX.writeFile(wb, `summint-animales-${new Date().toISOString().slice(0,10)}.xlsx`)
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner spinner-dark" style={{ margin: 'auto' }} /></div>

  const totalIngresos = finMeses.reduce((s, m) => s + m.ingresos, 0)
  const totalGastos = finMeses.reduce((s, m) => s + m.gastos, 0)
  const totalAnimales = animalesPorEspecie.reduce((s, a) => s + a.value, 0)

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">📈 Reportes</div>
          <div className="page-subtitle">Resúmenes operativos y financieros</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={exportarAnimales}>⬇️ Animales Excel</button>
          <button className="btn btn-primary" onClick={exportarExcel}>⬇️ Finanzas Excel</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        {[
          { label: 'Ingresos año', value: `$${totalIngresos.toLocaleString('es', { maximumFractionDigits: 0 })}`, icon: '💹', bg: '#dcfce7', color: 'var(--brand-600)' },
          { label: 'Gastos año', value: `$${totalGastos.toLocaleString('es', { maximumFractionDigits: 0 })}`, icon: '📉', bg: '#fee2e2', color: 'var(--accent-red)' },
          { label: 'Saldo neto', value: `$${(totalIngresos - totalGastos).toLocaleString('es', { maximumFractionDigits: 0 })}`, icon: '⚖️', bg: '#ede9fe', color: 'var(--accent-purple)' },
          { label: 'Animales activos', value: totalAnimales, icon: '🐄', bg: '#fef3c7', color: '#d97706' },
        ].map(s => (
          <div className="stat-card" key={s.label}>
            <div className="stat-icon" style={{ background: s.bg }}><span style={{ fontSize: 22 }}>{s.icon}</span></div>
            <div>
              <div className="stat-value" style={{ color: s.color, fontSize: 22 }}>{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid-2 mt-4">
        {/* Gráfico financiero */}
        <div className="card">
          <div className="card-header"><div className="card-title">Flujo financiero mensual</div></div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={finMeses}>
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={70} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v, n) => [`$${v.toLocaleString()}`, n === 'ingresos' ? 'Ingresos' : n === 'gastos' ? 'Gastos' : 'Neto']} />
              <Bar dataKey="ingresos" name="ingresos" fill="#22c55e" radius={[4,4,0,0]} />
              <Bar dataKey="gastos" name="gastos" fill="#f87171" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Animales por especie */}
        <div className="card">
          <div className="card-header"><div className="card-title">Composición del rodeo</div></div>
          {animalesPorEspecie.length === 0
            ? <div className="empty-state">Sin datos de animales</div>
            : (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={animalesPorEspecie} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={4} dataKey="value">
                    {animalesPorEspecie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Legend iconType="circle" iconSize={10} />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )
          }
        </div>
      </div>

      {/* Cultivos cosechados */}
      {cultivosStats.length > 0 && (
        <div className="card mt-4">
          <div className="card-header"><div className="card-title">Rendimiento de cultivos cosechados</div></div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Cultivo</th>
                  <th>Campañas</th>
                  <th>Hectáreas</th>
                  <th>Total kg cosechados</th>
                  <th>Promedio kg/ha</th>
                </tr>
              </thead>
              <tbody>
                {cultivosStats.map(c => (
                  <tr key={c.nombre}>
                    <td><strong>{c.nombre}</strong></td>
                    <td>{c.count}</td>
                    <td>{c.hectareas.toLocaleString('es', { maximumFractionDigits: 1 })} ha</td>
                    <td>{c.rendimiento.toLocaleString('es', { maximumFractionDigits: 0 })} kg</td>
                    <td>{c.hectareas > 0 ? (c.rendimiento / c.hectareas).toFixed(0) : '—'} kg/ha</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
